import React from 'react';
import {
  Create,
  SimpleForm,
  TextInput,
  SelectInput,
  DateTimeInput,
  required,
  useCreate,
  useNotify,
  useRedirect,
} from 'react-admin';
import {
  NotificationType,
  NotificationPriority,
} from '../../generated/data-contracts';

export const NotificationsCreate: React.FC = () => {
  const [create] = useCreate();
  const notify = useNotify();
  const redirect = useRedirect();

  const handleSubmit = async (data: any) => {
    try {
      // Transformar los datos antes de enviar
      const transformedData = {
        ...data,
        // Asegurar que expiresAt esté en formato ISO string completo si existe
        expiresAt: data.expiresAt
          ? (() => {
              if (data.expiresAt instanceof Date) {
                return data.expiresAt.toISOString();
              }
              // Si es string, necesitamos convertirlo al formato ISO completo
              if (typeof data.expiresAt === 'string') {
                let dateString = data.expiresAt;

                // Si no tiene segundos, agregarlos
                if (dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/)) {
                  dateString += ':00';
                }

                // Si no tiene milisegundos, agregarlos
                if (dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/)) {
                  dateString += '.000';
                }

                // Si no tiene timezone, agregar Z
                if (
                  !dateString.endsWith('Z') &&
                  !dateString.match(/[+-]\d{2}:\d{2}$/)
                ) {
                  dateString += 'Z';
                }

                // Validar que sea una fecha válida y convertir a ISO
                const date = new Date(dateString);
                if (isNaN(date.getTime())) {
                  console.error('Invalid date:', data.expiresAt);
                  return null;
                }

                return date.toISOString();
              }
              return data.expiresAt;
            })()
          : null,
      };

      await create(
        'notifications-admin',
        { data: transformedData },
        {
          onSuccess: data => {
            notify('Notificación creada correctamente', { type: 'success' });
            redirect('list', 'notifications-admin');
          },
          onError: (error: any) => {
            notify(`Error al crear la notificación: ${error.message}`, {
              type: 'error',
            });
          },
        }
      );
    } catch (error: any) {
      notify(`Error al crear la notificación: ${error.message}`, {
        type: 'error',
      });
    }
  };

  return (
    <Create title='Crear Nueva Notificación'>
      <SimpleForm onSubmit={handleSubmit}>
        <TextInput
          source='title'
          label='Título'
          validate={[required()]}
          fullWidth
        />

        <TextInput
          source='content'
          label='Contenido'
          multiline
          rows={4}
          validate={[required()]}
          fullWidth
        />

        <SelectInput
          source='type'
          label='Tipo'
          choices={[
            { id: NotificationType.Info, name: 'Información' },
            { id: NotificationType.Warning, name: 'Advertencia' },
            { id: NotificationType.Alert, name: 'Alerta' },
            { id: NotificationType.Success, name: 'Éxito' },
          ]}
          defaultValue={NotificationType.Info}
          validate={[required()]}
        />

        <SelectInput
          source='priority'
          label='Prioridad'
          choices={[
            { id: NotificationPriority.Low, name: 'Baja' },
            { id: NotificationPriority.Medium, name: 'Media' },
            { id: NotificationPriority.High, name: 'Alta' },
            { id: NotificationPriority.Critical, name: 'Crítica' },
          ]}
          defaultValue={NotificationPriority.Medium}
        />

        <DateTimeInput
          source='expiresAt'
          label='Fecha de Expiración'
          helperText='Opcional: Fecha y hora en que expira la notificación'
        />
      </SimpleForm>
    </Create>
  );
};
